import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@wwe-portal/ui/lib/lib';
import { LoadingIcon } from '../icons/loading-icon';
import gradientButtonStyles from './gradient-button.module.css';

const buttonVariants = cva(
  'select-none font-poppins border-0 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 duration-300',
  {
    variants: {
      variant: {
        none: '',
        secondary: 'bg-back200 hover:bg-text700 active:bg-text600 text-text300',
        orange:
          'bg-yellow800 text-white hover:bg-yellow600 active:bg-yellow500',
        orangeGradient: `${gradientButtonStyles['gradient-button']} text-white`,
        green: 'bg-green700 hover:bg-green600 active:bg-green500 text-white',
        blue: 'bg-blue700 border border-blue700 hover:bg-blue600 active:bg-blue500 text-white',
      },
      size: {
        default: 'h-[30px] py-2 px-6',
        large: 'h-10 py-[6px] px-5 text-base',
      },
    },
    defaultVariants: {
      variant: 'orange',
      size: 'default',
    },
  }
);

// background: linear-gradient(180deg, #FFD000 0%, #FFA500 7.5%, #CD7F32 21.5%, #D2691E 58%, #CD7F32 87.5%);

const loadingVariants = cva('bg-white', {
  variants: {
    variant: {
      none: 'text-white',
      orange: 'text-white',
      orangeGradient: 'text-white',
      green: 'text-white',
      blue: 'text-white',
      secondary: 'bg-inlineTextColor24',
    },
  },
  defaultVariants: {
    variant: 'none',
  },
});

export interface ButtonProps
  extends React.ComponentPropsWithoutRef<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: React.ReactNode;
  disabled?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      loading,
      disabled,
      loadingText,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          'cursor-pointer font-semibold',
          {
            'pointer-events-none': loading || disabled,
            'border-text500 bg-text500 text-white opacity-70': disabled,
          },
          className
        )}
        tabIndex={-1}
        {...props}
        ref={ref}
      >
        <div className="flex flex-row items-center gap-2">
          {loading ? (
            <>
              <LoadingIcon dotClassName={loadingVariants({ variant })} />
              {loadingText}
            </>
          ) : (
            <div>{children}</div>
          )}
        </div>
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
